import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Link, Redirect, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useCookies } from 'react-cookie';
import { NewApp } from './NewApp.js';
import { CLINICAL_PATHS, isClinicalAccountUser } from './helpers/clinical_account';
import { renderHeader } from './helpers/header_helper';
import { getPublicOnlyRedirectUrl, needsPhoneVerification } from './utils/userRedirect';
import { QuestionnaireModalProvider } from './utils/questionnaire_modal_context.js';
import { useTwoFactorAuth } from './hooks/useTwoFactorAuth.js';

import TwoFactorRenderer from './components/auth/two_factor_renderer';
import LoadingScreen from './components/system/loading_screen/index.jsx';
import Footer from './components/chrome/footer';
import About from './containers/about';
import Process from './containers/process';
import JointReplacement from './components/marketing/joint_replacement';
import Partners from './components/marketing/partners';
import Terms from './components/legal/terms';
import Privacy from './components/legal/privacy';
import CodeOfConduct from './components/legal/code_of_conduct';
import ReviewGuidelines from './components/legal/review_guidelines';
import PatientPartnerAgreement from './components/legal/patient_partner_agreement';
import ScrollToTop from './components/system/scroll_to_top';
import SendPageView from './components/system/send_page_view';
import ContactUs from './containers/contact_us';
import ReportAnEvent from './components/marketing/report_an_event';
import ForMentors from './components/marketing/for_mentors';
import PressRoom from './components/marketing/press_room';
import MetadataTags from './components/system/metadata_tags';
import AcceptPrivacyModal from './components/auth/accept_privacy_modal/index_gated';
import ConversationRoom from './containers/users/conversation_room';
import CallTokenConfirmation from './components/phone_calls/call_token_confirmation';
import CallSchedulePicker from './components/phone_calls/call_schedule_picker';
import CallReschedule from './components/phone_calls/call_reschedule';
import ClientContactConsent from './components/consent/client_contact_consent';
import CallsMenu from './components/phone_calls/calls_menu';
import PhoneCallsGuide from './components/phone_calls/phone_calls_guide';
import UsersSignIn from './components/auth/users_sign_in';
import VerifyPhone from './components/auth/verify_phone';
import UsersPasswordRecover from './components/auth/users_password_recover';
import UsersPasswordReset from './components/auth/users_password_reset';
import Context from './utils/context';
import Auth from './utils/auth';
import Viewport from './utils/viewport';
import ActionCable from './utils/actioncable';
import ProfileStatus from './utils/profile_status';
import FacebookConversionApi from './utils/facebook_conversion_api';
import ProviderHome from './components/providers/home';
import ProviderAccountDetails from './components/providers/account_details';
import ClinicalHome from './components/clinical/home';
import WeGiveBack from './components/marketing/we_give_back';
import GlobalPresence from './components/marketing/global_presence/global_presence';
import NotFoundPage from './components/system/not_found_page';
import ReferralPartnerAgreement from './components/legal/terms/pip';
import ServiceAgreement from './components/legal/terms/service_agreement';
import Baa from './components/legal/terms/baa';
import MemberPlatformNavWrap from './components/chrome/member_platform_nav_wrap';
import Settings, { ProfileEditSettings } from './components/settings';
import QuestionnaireModal from './components/ui_kit/modals/questionnaire_modal';
import AutoRedirect from './components/auth/auto_redirect';
import AdminSessionRedirect from './components/super_users/admin_session_redirect';
import ImpersonationBanner from './components/super_users/impersonation_banner';

import styles from './index.module.css';
import './App.css';
import './css/react-toastify.css';
import 'react-toastify/dist/ReactToastify.css';

const auth = new Auth(gon.current_user || {});
const viewport = new Viewport();
const profileStatus = new ProfileStatus();
const actioncable = new ActionCable();
const facebookConversionApi = new FacebookConversionApi();

const PublicRoute = ({ component: Component, path, layout: Layout }) => (
  <Route
    path={path}
    render={(props) => (
      <Layout>
        <Component path={path} {...props} />
      </Layout>
    )}
  />
);

const PrivateRoute = ({ component: Component, auth, path, layout: Layout }) => {
  const returnTo = window.location.pathname;
  const pathname = `/member/login?return_to=${encodeURIComponent(returnTo)}`;

  return (
    <QuestionnaireModalProvider>
      <QuestionnaireModal />
      <Route
        path={path}
        render={(props) => {
          if (!auth.isLoggedIn()) {
            return <Redirect to={pathname} />;
          }

          const currentUser = auth.getCurrentUser();
          const exempt = path === '/member/verify-phone';
          if (!exempt && needsPhoneVerification(currentUser, { impersonating: auth.isImpersonating() })) {
            return <Redirect to='/member/verify-phone' />;
          }

          return (
            <Layout>
              <Component currentUser={currentUser} {...props} />
            </Layout>
          );
        }}
      />
    </QuestionnaireModalProvider>
  );
};

const PublicOnlyRoute = ({ component: Component, auth, path, layout: Layout }) => {
  const redirectUrl = auth.isLoggedIn()
    ? getPublicOnlyRedirectUrl(auth.getCurrentUser(), { impersonating: auth.isImpersonating() })
    : '/member/conversations';

  return (
    <Route
      exact
      path={path}
      render={(props) =>
        auth.isLoggedIn() ? (
          <Redirect to={{ pathname: redirectUrl }} />
        ) : (
          <Layout>
            <Component path={path} {...props} />
          </Layout>
        )
      }
    />
  );
};

const ProviderOnlyRoute = ({ component: Component, auth, path, layout: Layout }) => {
  const returnTo = window.location.pathname;
  const pathname = `/member/login?return_to=${encodeURIComponent(returnTo)}`;

  return (
    <Route
      path={path}
      render={(props) => {
        if (!auth.isLoggedIn()) {
          return <Redirect to={pathname} />;
        }

        const currentUser = auth.getCurrentUser();
        if (isClinicalAccountUser(currentUser)) {
          return <Redirect to={CLINICAL_PATHS.programInsights} />;
        }

        if (currentUser.current_profile_type === 'AccountAdmin') {
          return (
            <Layout>
              <Component currentUser={currentUser} {...props} />
            </Layout>
          );
        }

        return <Redirect to={pathname} />;
      }}
    />
  );
};

const ClinicalOnlyRoute = ({ component: Component, auth, path, layout: Layout }) => {
  const returnTo = window.location.pathname;
  const pathname = `/member/login?return_to=${encodeURIComponent(returnTo)}`;

  return (
    <Route
      path={path}
      render={(props) => {
        if (!auth.isLoggedIn()) {
          return <Redirect to={pathname} />;
        }

        const currentUser = auth.getCurrentUser();
        if (needsPhoneVerification(currentUser, { impersonating: auth.isImpersonating() })) {
          return <Redirect to='/member/verify-phone' />;
        }

        if (isClinicalAccountUser(currentUser)) {
          return (
            <Layout>
              <Component currentUser={currentUser} {...props} />
            </Layout>
          );
        }

        return <Redirect to={getPublicOnlyRedirectUrl(currentUser, { impersonating: auth.isImpersonating() })} />;
      }}
    />
  );
};

const TwoFactorRoute = ({ path, layout: Layout }) => {
  const {
    isValidating,
    authState,
    recoveryCodes,
    completedUser,
    handleRegistrationComplete,
    updateAuthState,
    clearRecoveryData,
  } = useTwoFactorAuth();

  return (
    <Route
      path={path}
      render={(props) => {
        if (isValidating) {
          return (
            <Layout>
              <LoadingScreen />
            </Layout>
          );
        }

        if (!authState?.success) {
          return <Redirect to='/member/login' />;
        }

        return (
          <Layout>
            <TwoFactorRenderer
              mode={authState.required_action}
              routeProps={props}
              recoveryCodes={recoveryCodes}
              completedUser={completedUser}
              onRegistrationComplete={handleRegistrationComplete}
              onUpdateAuthState={updateAuthState}
              onClearRecoveryData={clearRecoveryData}
            />
          </Layout>
        );
      }}
    />
  );
};

const PublicLayout = (props) => (
  <>
    {renderHeader(auth)}
    {props.children}
    {gon.embedded ? (
      <></>
    ) : (
      <Footer
        fullFooter={
          (window.location.pathname.includes('mentor-match') && window.location.pathname.includes('surgeon')) ||
          window.location.pathname.includes('report-an-event')
            ? false
            : true
        }
      />
    )}
  </>
);

const PortalLayout = (props) => (
  <>
    <div
      style={{
        backgroundImage: 'url(https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/registerbg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      className='container-fluid sign-in-outer'
    >
      <Link to='/'>
        <img
          className='signin-logo'
          src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/whlogo.svg'
        />
      </Link>
      {props.children}
    </div>
  </>
);

const PlatformLayout = (props) => (
  <div className={styles.platform_layout}>
    {renderHeader(auth)}
    {props.children}
  </div>
);

const BareLayout = (props) => <>{props.children}</>;

const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_ad_content'),
  };
};

function App() {
  const [cookies, setCookie] = useCookies(['iglive', 'utm_params']);

  useEffect(() => {
    // Capture and store UTM parameters
    const utmParams = getUTMParams();

    if (
      (utmParams.utm_source !== null ||
        utmParams.utm_medium !== null ||
        utmParams.utm_campaign !== null ||
        utmParams.utm_term !== null ||
        utmParams.utm_content !== null) &&
      !auth.isLoggedIn()
    ) {
      // Store UTM parameters in a cookie if they exist and the user is not already logged in
      setCookie('utm_params', JSON.stringify(utmParams), { path: '/', maxAge: 30 * 24 * 60 * 60 });
    }
  }, []);

  return (
    <Context.Provider
      value={{
        auth: auth,
        viewport: viewport,
        profileStatus: profileStatus,
        actioncable: actioncable,
        facebookConversionApi: facebookConversionApi,
      }}
    >
      <Router>
        <ScrollToTop />
        <SendPageView />
        <MetadataTags />
        <div className='App' id='topOfPage'>
          <ImpersonationBanner />
          <AcceptPrivacyModal />
          <ToastContainer />
          {auth.isLoggedIn() && <GlobalPresence />}
          <Switch>
            <PublicRoute exact path='/calls/reschedule/:token' component={CallReschedule} layout={PublicLayout} />
            <PublicRoute
              exact
              path='/calls/:action(schedule|accept|decline)/:token'
              component={CallSchedulePicker}
              layout={PublicLayout}
            />
            <PublicRoute
              exact
              path='/calls/:action(cancel)/:token'
              component={CallTokenConfirmation}
              layout={PublicLayout}
            />
            <PublicRoute exact path='/consent/:token' component={ClientContactConsent} layout={BareLayout} />
            <TwoFactorRoute exact path='/auth/2fa' layout={PortalLayout} />
            <PublicRoute exact path='/who-we-are' component={About} layout={PublicLayout} />
            <PublicRoute exact path='/we-give-back' component={WeGiveBack} layout={PublicLayout} />
            <PublicRoute exact path='/how-it-works' component={Process} layout={PublicLayout} />
            <PublicRoute
              exact
              path='/procedures/joint-replacement'
              component={JointReplacement}
              layout={PublicLayout}
            />
            {/* <ProviderOnlyRoute path='/provider' auth={auth} component={ProviderHome} layout={PlatformLayout} /> */}
            <Route
              exact
              path='/get-started/patient-partner-match'
              render={() => <Redirect to='/member/join/pre-signup' />}
            />
            <Route exact path='/get-started' render={() => <Redirect to='/member/join/pre-signup' />} />
            <Route exact path='/get-started/surgeon-match' render={() => <Redirect to='/member/join/pre-signup' />} />
            <PublicRoute exact path='/partners' component={Partners} layout={PublicLayout} />
            <PublicRoute exact path='/terms' component={Terms} layout={PublicLayout} />
            <PublicRoute exact path='/terms/pip' component={ReferralPartnerAgreement} layout={PublicLayout} />
            <PublicRoute exact path='/terms/service-agreement' component={ServiceAgreement} layout={PublicLayout} />
            <PublicRoute exact path='/terms/baa' component={Baa} layout={PublicLayout} />
            <PublicRoute exact path='/privacy' component={Privacy} layout={PublicLayout} />
            <PublicRoute exact path='/code-of-conduct' component={CodeOfConduct} layout={PublicLayout} />
            <PublicRoute exact path='/review-guidelines' component={ReviewGuidelines} layout={PublicLayout} />
            <PublicRoute
              exact
              path='/patient-partner-agreement'
              component={PatientPartnerAgreement}
              layout={PublicLayout}
            />
            <PublicRoute exact path='/press-room' component={PressRoom} layout={PublicLayout} />
            <PublicRoute exact path='/contact-us' component={ContactUs} layout={PublicLayout} />
            <PublicRoute exact path='/report-an-event' component={ReportAnEvent} layout={PublicLayout} />
            <PublicRoute exact path='/for-mentors' component={ForMentors} layout={PublicLayout} />
            <Route exact path='/member/phone_calls' render={() => <Redirect to='/member/phone_calls/mentee' />} />
            <PublicRoute
              exact
              path='/member/phone_calls/:role(mentee|mentor)'
              component={PhoneCallsGuide}
              layout={BareLayout}
            />
            <PrivateRoute exact path='/member/calls' auth={auth} component={CallsMenu} layout={MemberPlatformNavWrap} />
            <PrivateRoute
              exact
              path={`/member/conversations/:conversation_id`}
              auth={auth}
              component={ConversationRoom}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path={`/member/conversations`}
              auth={auth}
              component={ConversationRoom}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute exact path='/member/verify-phone' auth={auth} component={VerifyPhone} layout={PortalLayout} />
            <PublicOnlyRoute exact path='/member/login' auth={auth} component={UsersSignIn} layout={PortalLayout} />
            <PublicRoute exact path='/member/forgot-password' component={UsersPasswordRecover} layout={PortalLayout} />
            <PublicRoute exact path='/member/change-password' component={UsersPasswordReset} layout={PortalLayout} />
            <PrivateRoute
              exact
              path='/auth/sign-in-token'
              auth={auth}
              component={AutoRedirect}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path='/member/admin-session'
              auth={auth}
              component={AdminSessionRedirect}
              layout={BareLayout}
            />
            <PrivateRoute
              exact
              path={`/member/profile/settings`}
              auth={auth}
              component={Settings}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path={`/member/profile/edit`}
              auth={auth}
              component={ProfileEditSettings}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path={`/member/settings/account`}
              auth={auth}
              component={Settings}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path={`/member/settings/:section`}
              auth={auth}
              component={Settings}
              layout={MemberPlatformNavWrap}
            />
            <PrivateRoute
              exact
              path={`/member/settings`}
              auth={auth}
              component={Settings}
              layout={MemberPlatformNavWrap}
            />
            {/* Provider Routes Start  */}
            <ProviderOnlyRoute
              exact
              path='/provider/account-details'
              auth={auth}
              component={ProviderAccountDetails}
              layout={PlatformLayout}
            />
            <ProviderOnlyRoute
              exact
              path={[
                '/provider/insights',
                '/provider/mentor-connect',
                '/provider/account-details',
                '/provider/mentor-insights',
                '/provider/patient-demographics',
                '/provider/calls',
              ]}
              auth={auth}
              component={ProviderHome}
              layout={PlatformLayout}
            />
            {/* Provider Routes End  */}

            {/* Clinical Routes Start  */}
            <ClinicalOnlyRoute
              exact
              path={[
                CLINICAL_PATHS.root,
                CLINICAL_PATHS.programInsights,
                CLINICAL_PATHS.studyInsights,
                CLINICAL_PATHS.retentionInsights,
                CLINICAL_PATHS.reportableEvents,
              ]}
              auth={auth}
              component={ClinicalHome}
              layout={PlatformLayout}
            />
            {/* Clinical Routes End  */}

            <Route
              path={[
                `/`,
                `/member/join`,
                `/member/join/support`,
                `/member/join/pre-signup`,
                `/member/join/support/*`,
                `/member/profile/:id`,
                `/member/profile/:id/:view(post|about-me)`,
                `/member/mentor-match`,
              ]}
              component={NewApp}
            />
            <PublicRoute path='*' auth={auth} component={NotFoundPage} layout={PublicLayout} />
          </Switch>
        </div>
      </Router>
    </Context.Provider>
  );
}

export default App;
